import { useMutation } from "@apollo/client/react"; import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Grid } from "@mui/material";
import CardBox from "../../CardBox/CardBox";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import SAVE_VISITA_PROCEDIMIENTO_AP, { adaptAddVisitaPresencialAPProcedimientoInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProcedimientoAP";
import { useVisita } from "../../../../../../context/VisitaContext";
import { useStepSave } from "../../../../hooks/useStepSave";
import { PROCEDIMIENTO_CONEXION_ID, PROCEDIMIENTO_DESCONEXION_ID } from "../../../../constants/procedimientoConexionIds";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import { handleChangeImageVisita } from "../../../../utils/handleChangeImageVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";

export default function ProcedimientoAP({ esUrgencia, readOnly, onSaveRef, onExposeFormatted }) {
  const [formData, setFormData] = useState({});
  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const { deleteArchivo } = useDeleteArchivoVisita();

  const handleChangeFile = useCallback((name, fileValue) => {
    handleChangeImageVisita({
      saveArchivoVisita,
      name,
      fileValue,
      idVisita: visita.idVisita,
      handleChange,
      idTipoArchivo: tipoArchivoVisita.TROQUELES
    });
  }, [visita.idVisita, saveArchivoVisita]);

  const onDeleteImage = (name) => {
    const file = formData[name];
    deleteArchivo({
      idArchivo: file.idArchivo,
      name: name,
      handleChange
    });
  };

  //QUERY y MUTATION
  const [save] = useMutation(SAVE_VISITA_PROCEDIMIENTO_AP);

  const formattedInputs = useMemo(() => {
    return inputs.map((input) => {
      const newInput = { ...input };

      switch (input.name) {
        case "tensionArterial":
          newInput.required = visita?.edad > 15;
          break;
        case "obsAlmacenamientoNo":
          newInput.hidden = formData?.condicionAlmacenamiento;
          break;
        case "obsStockNo":
          newInput.hidden = formData?.stockProductos;
          break;
      }

      if (esUrgencia) newInput.required = false;

      if (readOnly) newInput.disabled = true;
      return newInput;
    });
  }, [readOnly, esUrgencia, formData?.condicionAlmacenamiento, formData?.stockProductos, visita?.edad]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (name === 'condicionAlmacenamiento' && !!value)
      handleChange('obsAlmacenamientoNo', null);

    if (name === 'stockProductos' && !!value)
      handleChange('obsStockNo', null);
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedInputs, formData });

    const { data } = await save({ variables: { input: adaptAddVisitaPresencialAPProcedimientoInput(formData) } });

    const idPresencialAP = data?.saveVisitaPresencialAPProcedimiento?.idPresencialAP;
    if (!idPresencialAP) throw new Error("Error al guardar");

    setVisita((prev) => ({
      ...prev,
      ...formData,
      idPresencialAP: idPresencialAP
    }));

  });

  useEffect(() => {
    setFormData(visita);
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: 'procedimientoAP',
      stepName: 'Procedimiento AP',
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  return (
    <CardBox>
      <Grid container spacing={2}>
        {formattedInputs.
          filter((input) => !input.hidden).
          map((input) =>
            <Grid size={{ xs: input.halfSpace ? 6 : 12 }} key={input.name}>
              <RenderInputByTypeMobile
                key={`${input.name}-${input.type}${input.type === 'file' ? formData[input.name]?.titulo : ''}`}
                label={input.displayName}
                value={formData[input.name]}
                handleChange={input.type === 'file' ? handleChangeFile : handleChange}
                handleDelete={input.type === 'file' ? onDeleteImage : null}
                row={input.type === "multiple_boolean"}
                input={input} />
            </Grid>
          )}
      </Grid>
    </CardBox>);

}
const inputs = [
  {
    name: "tensionArterial",
    displayName: "Tensión arterial",
    type: "string",
    halfSpace: true
  },
  {
    name: "temperatura",
    displayName: "Temperatura",
    type: "float",
    required: true,
    halfSpace: true
  }, ,

  {
    name: "frecuenciaCardiaca",
    displayName: "Frecuencia Cardiaca",
    type: "float",
    required: true,
    halfSpace: true
  },
  {
    name: "frecuenciaRespiratoria",
    displayName: "Frecuencia Respiratoria",
    type: "float",
    required: true,
    halfSpace: true
  },
  {
    name: "procedimiento",
    displayName: "Procedimiento",
    type: "checkbox",
    options: [
      { value: PROCEDIMIENTO_CONEXION_ID, label: "Conexión" },
      { value: PROCEDIMIENTO_DESCONEXION_ID, label: "Desconexión" }],

    lightTitle: true,
    required: true
  },
  {
    name: "troquelAdjunto",
    displayName: "Troquel (adjuntar)",
    type: "file",
    multiple: false,
    combineAsPdf: false,
    validExtensions: ['PNG', 'JPG', 'HEIC', 'JPEG', 'JFIF']
  },
  {
    name: "curacionCateter",
    displayName: "Curación de Catéter",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "cambioBionector",
    displayName: "Cambio de Bionector",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "controlBomba",
    displayName: "Control de Bomba",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "condicionAlmacenamiento",
    displayName: "Condición de almacenamiento",
    type: "multiple_boolean",
    lightTitle: true,
    required: true,
    options: [
      { value: true, label: "Óptimo" },
      { value: false, label: "No Óptimo" }]

  },
  {
    name: "obsAlmacenamientoNo",
    displayName: "Obs. No Óptimo",
    type: "long_string",
    maxLength: 4000,
    required: true,
    onlyWhen: { field: "condicionAlmacenamiento", value: "no_optimo" }
  },
  {
    name: "stockProductos",
    displayName: "Stock de Productos",
    type: "multiple_boolean",
    lightTitle: true,
    required: true,
    options: [
      { value: true, label: "Óptimo" },
      { value: false, label: "No Óptimo" }]

  },
  {
    name: "obsStockNo",
    displayName: "Obs. No Óptimo",
    type: "long_string",
    maxLength: 4000,
    required: true,
    onlyWhen: { field: "stockProductos", value: "no_optimo" }
  }];